import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as ArrowBackIcon } from 'assets/icons/arrow-back.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import useInventoryRequestContext from 'contexts/inventory-stock.context/context';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { COKITCHEN_ID, MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { unwrapAmount } from 'utils/functions';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const InventoryRequestPreview = () => {
  const navigate = useNavigate();
  const { requests, setScrollIndex } = useInventoryRequestContext();
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  const router = useNavigate();

  const total = useMemo(() => {
    return requests.reduce((sum, request) => {
      const price = unwrapAmount(request.vendor.price);
      const quantity = Number(request.quantity);
      return sum + price * quantity;
    }, 0);
  }, [requests]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to procurement');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const handleClick = (index: number) => {
    setScrollIndex(index);
    navigate(-1);
  };

  const data = {
    cokitchen_id: COKITCHEN_ID,
    title: '.',
    note: '.',
    items: requests.map((request) => {
      return {
        ingredient_id: request.id,
        vendor_id: request.vendor.id,
        quantity: Number(request.quantity)
      };
    })
  };

  const createStockRequest = async () => {
    if (!canWriteInventoryManagement) return;
    try {
      setIsCreatingRequest(true);
      const response = await apiService.post('main-store/procurements', { data });
      if (response) {
        router(MAIN_STORE_ROUTE_KEYS.INVENTORY_STOCK);
        toast(<ToastMessage message='Procurement request created successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error creating procurement request'}
          type='error'
        />
      );
    } finally {
      setIsCreatingRequest(false);
    }
  };

  return (
    <div className='ml-2 md:ml-8 request'>
      <h4 className='text-[24px] font-semibold text-[#2E3031]'>Make a request</h4>
      <p className='text-[#6F7376] text-sm mb-8'>
        Inventory running low on selected items? prepare your list
      </p>
      <div className='bg-[#F7F7F8] w-full max-w-[678px] h-auto p-1 flex  justify-center items-center'>
        <div className='bg-white w-full maxw-[670px] h-auto rounded-md py-6'>
          <div className='px-4 md:px-7 '>
            <div className=''>
              <h4 className='text-[24px] font-semibold text-[#2E3031]'>Review selections</h4>
              <p className='text-[#6F7376] text-sm'>Ensure you have all items needed selected</p>
            </div>
            {requests.map((request, index) => (
              <div
                className='flex justify-between border-b border-b-[#EAEBEB] py-6 gap-6 items-center'
                key={index}>
                <div>
                  <p className='font-medium text-[#2E3031] capitalize'>{request.name}</p>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <p className='text-sm font-medium text-[#2E3031]'>
                      {request.quantity}
                      {request.measurement}
                    </p>
                    <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                    <div className='flex flex-col'>
                      <div className='flex items-center gap-1'>
                        <p className='text-[#6F7376] text-sm font-medium'>{request.vendor.name}</p>
                        <div className='bg-[#DCDBD5] w-[5px] h-[5px] rounded-full' />
                        <p className='text-[#6F7376] text-sm font-medium'>
                          ₦{' '}
                          {(
                            unwrapAmount(request.vendor.price) * Number(request.quantity)
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {canWriteInventoryManagement ? (
                  <EditIcon onClick={() => handleClick(index)} className='cursor-pointer' />
                ) : null}
              </div>
            ))}
            <div className='flex justify-end gap-4 mt-6 items-center mb-[40px]'>
              <p className='text-sm text-[#6F7376]'>Total</p>
              <h4 className='text-[#2E3031] text-2xl font-semibold'>N {total.toLocaleString()}</h4>
            </div>
          </div>
          <div className='flex gap-6 pt-7 px-4 md:px-7  items-center justify-between border-t-2 border-t-[#F7F7F8] w-full flex-wrap-reverse'>
            <div
              className='flex items-center gap-3 cursor-pointer'
              onClick={() => {
                navigate(-1);
              }}>
              <div className='flex w-[30px] h-[30px] bg-[#F4F5F5] rounded-full justify-center items-center'>
                <ArrowBackIcon className='icon' />
              </div>
              <p className='text-sm text-[#2E3031]'>Back</p>
            </div>
            <div className='flex gap-6 items-center'>
              {/* <p className='text-sm font-medium'>Cancel</p> */}
              {canWriteInventoryManagement && (
                <Button
                  variant='block'
                  color='primary'
                  small
                  className='w-[153px]'
                  loading={isCreatingRequest}
                  onClick={() => createStockRequest()}>
                  Confirm
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryRequestPreview;
